import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Checkbox, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import agreementContent from '../../../components/subscriber-agreement/subscriber-agreement.json';

const SCROLL_THRESHOLD = 24;
const CONTENT_PAD_X = '36px';
const CONTENT_PAD_Y = '24px';

const modalShellSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(920px, 92vw)',
  height: 'min(90vh, 900px)',
  maxHeight: '90vh',
  bgcolor: '#0d2147',
  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.55)',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '4px',
  outline: 'none',
  overflow: 'hidden',
  minHeight: 0,
  border: '1px solid rgba(255, 255, 255, 0.16)',
  boxSizing: 'border-box',
};

const innerPadSx = {
  boxSizing: 'border-box',
  px: CONTENT_PAD_X,
  py: CONTENT_PAD_Y,
  width: '100%',
};

const bodyTextSx = {
  fontSize: '14px',
  lineHeight: 1.7,
  color: 'rgba(255, 255, 255, 0.92)',
  '& a': {
    color: '#FFB36B',
    textDecoration: 'underline',
    '&:hover': {
      color: '#FF944D',
    },
  },
};

const AgreementSection = ({ section }) => {
  const blocks = [];
  let bulletGroup = [];

  const flushBullets = (key) => {
    if (bulletGroup.length === 0) return;
    blocks.push(
      <Box
        key={key}
        component="ul"
        sx={{
          mt: 1.25,
          mb: 0,
          pl: '22px',
          pr: 0,
          listStyleType: 'disc',
        }}
      >
        {bulletGroup.map((bullet, index) => (
          <Box
            key={`${key}-b-${index}`}
            component="li"
            sx={{ mt: index === 0 ? 0 : 0.75 }}
          >
            <Box
              component="span"
              sx={bodyTextSx}
              dangerouslySetInnerHTML={{ __html: bullet }}
            />
          </Box>
        ))}
      </Box>
    );
    bulletGroup = [];
  };

  section.content.forEach((item, index) => {
    if (item.type === 'bullet') {
      bulletGroup.push(item.text);
      return;
    }
    flushBullets(`bullets-before-${index}`);
    blocks.push(
      <Box
        key={`p-${index}`}
        sx={{
          mt: 1.75,
          whiteSpace: 'pre-line',
        }}
      >
        <Box
          component="span"
          sx={bodyTextSx}
          dangerouslySetInnerHTML={{ __html: item.text }}
        />
      </Box>
    );
  });
  flushBullets('bullets-final');

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="titleSmall"
        sx={{ fontWeight: 700, mt: 3, mb: 1.25, lineHeight: 1.4 }}
      >
        {section.title}
      </Typography>
      {blocks}
    </Box>
  );
};

const checkboxLabelSx = {
  fontSize: '12px',
  lineHeight: 1.45,
  color: 'rgba(255, 255, 255, 0.9)',
};

const AgreementCheckboxRow = ({
  label,
  checked,
  disabled,
  onChange,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      mb: 1,
      p: '8px 10px',
      borderRadius: '4px',
      bgcolor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      boxSizing: 'border-box',
    }}
  >
    <Checkbox
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      size="small"
      sx={{
        p: 0,
        mt: '1px',
        color: '#EC6C10',
        '&.Mui-checked': { color: '#EC6C10' },
      }}
      inputProps={{
        'aria-label': label,
      }}
    />
    <Box sx={{ ...checkboxLabelSx, flex: 1 }}>{label}</Box>
  </Box>
);

/**
 * Non-dismissible subscriber agreement modal.
 * User must scroll to the bottom, check both boxes, and click I Accept.
 */
const SubscriberAgreementPopup = ({
  open,
  onAccept,
  accepting = false,
  readOnly = false,
  onClose,
}) => {
  const scrollRef = useRef(null);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [checkedStates, setCheckedStates] = useState({});

  const checkboxes = agreementContent.checkboxes;
  const allChecked = checkboxes.every((cb) => checkedStates[cb.id]);
  const canAccept = !readOnly && hasReachedBottom && allChecked && !accepting;

  const updateScrollState = useCallback(() => {
    if (readOnly) return;
    const el = scrollRef.current;
    if (!el) return;
    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;
    setHasReachedBottom(atBottom);
  }, [readOnly]);

  useEffect(() => {
    if (!open) {
      setHasReachedBottom(false);
      setCheckedStates({});
      return;
    }

    if (readOnly) return;

    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [open, readOnly, updateScrollState]);

  const handleCheckboxChange = (id) => (event) => {
    setCheckedStates((prev) => ({ ...prev, [id]: event.target.checked }));
  };

  const handleAccept = () => {
    if (!canAccept) return;
    onAccept();
  };

  const handleCloseRequest = () => {
    if (readOnly && onClose) onClose();
  };

  const handleModalClose = (_event, reason) => {
    if (!readOnly) {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      return;
    }
    handleCloseRequest();
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      disableEscapeKeyDown={!readOnly}
      aria-labelledby="subscriber-agreement-title"
      sx={{ zIndex: 1400 }}
    >
      <Box sx={modalShellSx}>
        <Box
          sx={{
            flexShrink: 0,
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
            background:
              'linear-gradient(135deg, rgba(13, 32, 63, 0.98), rgba(22, 53, 105, 0.98))',
          }}
        >
          <Box
            sx={{
              ...innerPadSx,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              position: 'relative',
              pr: readOnly ? '56px' : CONTENT_PAD_X,
            }}
          >
            {readOnly ? (
              <IconButton
                aria-label="Close agreement"
                onClick={handleCloseRequest}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  color: 'rgba(255, 255, 255, 0.85)',
                  '&:hover': {
                    color: '#fff',
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
            <Typography
              component="div"
              variant="overline"
              sx={{
                color: '#FFB36B',
                fontWeight: 700,
                letterSpacing: 1.4,
                lineHeight: 1,
                display: 'block',
                width: '100%',
              }}
            >
              {readOnly ? 'Subscriber agreement' : 'Required agreement'}
            </Typography>
            <Typography
              component="div"
              id="subscriber-agreement-title"
              variant="titleSmall"
              sx={{
                display: 'block',
                width: '100%',
                fontWeight: 700,
                mt: 1,
                lineHeight: 1.35,
                color: '#fff',
              }}
            >
              {agreementContent.title}
            </Typography>
            <Typography
              component="div"
              variant="descriptionMedium"
              sx={{
                display: 'block',
                width: '100%',
                color: 'rgba(255, 255, 255, 0.78)',
                mt: 1.25,
                lineHeight: 1.5,
              }}
            >
              {readOnly
                ? 'Scroll through the complete agreement below.'
                : 'Scroll through the complete agreement, then confirm both declarations to continue.'}
            </Typography>
          </Box>
        </Box>

        <Box
          ref={scrollRef}
          onScroll={updateScrollState}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            boxSizing: 'border-box',
            '&::-webkit-scrollbar': { width: 10 },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#EC6C10',
              borderRadius: 8,
            },
          }}
        >
          <Box sx={{ ...innerPadSx, pt: '28px', pb: '28px' }}>
            {agreementContent.preamble.map((paragraph, index) => (
              <Box
                key={`pre-${index}`}
                sx={{ ...bodyTextSx, mt: index === 0 ? 0 : 1.75 }}
              >
                {paragraph}
              </Box>
            ))}

            {agreementContent.sections.map((section) => (
              <AgreementSection key={section.title} section={section} />
            ))}

            {!readOnly ? (
              <Box
                sx={{
                  mt: 3,
                  pt: 2.5,
                  borderTop: '1px solid rgba(255, 255, 255, 0.14)',
                }}
              >
                <Typography
                  component="div"
                  variant="descriptionMedium"
                  sx={{
                    display: 'block',
                    fontSize: '13px',
                    color: hasReachedBottom
                      ? '#7CE3A3'
                      : 'rgba(255, 255, 255, 0.78)',
                    mb: 1.5,
                  }}
                >
                  {hasReachedBottom
                    ? 'Please confirm both declarations below.'
                    : 'Please scroll to agree.'}
                </Typography>

                {checkboxes.map((checkbox) => (
                  <AgreementCheckboxRow
                    key={checkbox.id}
                    label={checkbox.label}
                    checked={!!checkedStates[checkbox.id]}
                    disabled={!hasReachedBottom}
                    onChange={handleCheckboxChange(checkbox.id)}
                  />
                ))}
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            borderTop: '1px solid rgba(255, 255, 255, 0.14)',
            bgcolor: '#071733',
          }}
        >
          <Box sx={{ ...innerPadSx, py: '16px' }}>
            {readOnly ? (
              <Button
                variant="contained"
                fullWidth
                onClick={handleCloseRequest}
                sx={{
                  height: 44,
                  fontSize: 15,
                  fontWeight: 700,
                  borderRadius: '4px',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #EC6C10, #FF944D)',
                  boxShadow: '0 12px 28px rgba(236, 108, 16, 0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #EC6C10, #FF944D)',
                    opacity: 0.9,
                  },
                }}
              >
                Close
              </Button>
            ) : (
              <Button
                variant="contained"
                fullWidth
                disabled={!canAccept}
                onClick={handleAccept}
                sx={{
                  height: 44,
                  fontSize: 15,
                  fontWeight: 700,
                  borderRadius: '4px',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #EC6C10, #FF944D)',
                  boxShadow: canAccept
                    ? '0 12px 28px rgba(236, 108, 16, 0.35)'
                    : 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #EC6C10, #FF944D)',
                    opacity: 0.9,
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.45)',
                    background: 'rgba(255, 255, 255, 0.12)',
                  },
                }}
              >
                {accepting ? 'Submitting…' : agreementContent.acceptButtonLabel}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SubscriberAgreementPopup;
